import { Box, Button, Divider, FormControl, FormHelperText, IconButton, InputAdornment, InputLabel, OutlinedInput, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { Toast, ToastControls } from "daymap-react";
import { useQuery } from "@tanstack/react-query";

export function PasswordReset(){
    const navigate = useNavigate();
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [confirm, setConfirm] = useState("");
    const [showConfirm, setShowConfirm] = useState(false);
    const [showError, setShowError] = useState(false);
    const [error, setError] = useState("Passwords do not match");
    const [searchParams] = useSearchParams();
    const [valid, setValid] = useState(false);
    const ToastRef = useRef<ToastControls>(null);
    const [serverError, setServerError] = useState("");
    const [loading, setLoading] = useState(false);

    const {data: passwordPolicy} = useQuery({
        queryKey: ['passwordPolicy'],
        queryFn: async () => {
            const url = new URL(window.location.href);
            const response = await fetch(`https://${url.host}/lms/api/password/policy`);
            if (!response.ok) {
                throw new Error('Failed to fetch password policy');
            }
            return await response.json();
        },
        staleTime: 1000 * 60 * 10
    });

    useEffect(() => {
        if(!searchParams.get("token"))
        {
            navigate("/");
        }
    }, [searchParams])

    const ResetPassword = async () => {
        setLoading(true);
        //validate password
        const regex = new RegExp(passwordPolicy?.Policy ?? "");
        if(!regex.test(password)) {
            setShowError(true);
            setError(passwordPolicy?.PolicyMessage ?? "Password is not complex enough.");
            setLoading(false);
            return;
        }

        const url = new URL(window.location.href);

        try 
        {
            const response = await fetch(
                `https://${url.host}/lms/api/password/update`, 
                {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                  },
                body: JSON.stringify({Token:searchParams.get("token"), Password:password})
            });

            const data = await response.json();
            if(!response.ok){
                throw Error((data.message != undefined ? data.message : data.title) ?? "")
            }
            setServerError(data.message);
            ToastRef.current?.openToast();
            setTimeout(() => {navigate("/")}, 1500);
        }
        catch(ex:any){
            setServerError(ex.message);
            ToastRef.current?.openToast();
        }
        setShowError(false);
        setLoading(false);
    }


    return (
            <Box 
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100%',
                    textAlign: 'center'
                }}
            >
                <Box 
                    sx={{
                        display: 'flex',
                        flexDirection:'column',
                        gap:1,
                        alignItems: 'center',
                        justifyContent: 'center',
                        textAlign: 'center',
                    }}
                >
                    <Toast ref={ToastRef} type={serverError == "" ? "success" : "error"} message={serverError == "" ? "Password has been reset" : serverError} />
                    <Typography fontSize="2rem">Reset your password</Typography>
                    <Box 
                        sx={{
                            display: 'flex',
                            flexDirection:'column',
                            gap:1,
                            alignItems: 'center',
                            justifyContent: 'center',
                            textAlign: 'center',
                            width:300
                        }}
                    >
                        <FormControl sx={{width:"100%", marginTop:4}}>
                            <InputLabel htmlFor="password-input">New Password</InputLabel>
                            <OutlinedInput 
                                id="password-input"                              
                                label="New Password" 
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setPassword(event.target.value);
                                    if(showError && event.target.value == confirm)
                                        setShowError(false);
                                    setValid(event.target.value != "" &&event.target.value == confirm);
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                        showPassword ? 'hide the password' : 'display the password'
                                        }
                                        onClick={() => {setShowPassword((show) => !show)}}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                }
                            />
                        </FormControl>
                        <FormControl sx={{width:"100%"}} error={showError}>
                            <InputLabel htmlFor="password-confirm">Confirm</InputLabel>
                            <OutlinedInput
                                id="password-confirm" 
                                label="Confirm" 
                                type={showConfirm ? 'text' : 'password'}
                                value={confirm}
                                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                                    setConfirm(event.target.value);
                                    if(showError && event.target.value == password)
                                        setShowError(false);
                                    setValid(event.target.value != "" &&event.target.value == password);
                                }}
                                endAdornment={
                                    <InputAdornment position="end">
                                    <IconButton
                                        aria-label={
                                            showConfirm ? 'hide the password' : 'display the password'
                                        }
                                        onClick={() => setShowConfirm((show) => !show)}
                                        edge="end"
                                    >
                                        {showConfirm ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                    </InputAdornment>
                                }
                                onBlur={() => {setShowError(confirm != password); if(confirm != password){setError("Passwords do not match")}}}
                                error={showError}
                            />
                         {showError && <FormHelperText>{error}</FormHelperText>}
                        </FormControl>
                        <Button loading={loading} loadingPosition="end" sx={{width:"100%",marginTop: showError ? error.length > 30 ? 0 : 5 : 8}} variant="contained" disabled={!valid} onClick={ResetPassword}>Reset password</Button>
                        <Button  sx={{width:"100%"}} variant="outlined" onClick={() => navigate("/")}>Sign in</Button>
                        <Divider  sx={{width:"100%", marginTop:2}}/>
                        <Typography variant="caption" sx={{textAlign:"left"}}>
                            For your privacy, we take security seriously. Suspicious activity, such as requests from unfamiliar devices or locations,
                            may result in the reset being invalidated. If you have any issues resetting your password please contact your school.
                        </Typography>
                    </Box>
                </Box>
            </Box>
    )
}